/*
 * lazy-video.js
 * Poster-first, play-in-viewport videos. Handles two markup styles:
 *   1. Custom banners: <video data-lazy-video preload="none" poster="..."> with
 *      <source data-src="..."> (source URL withheld until first view).
 *   2. Product gallery videos rendered by Liquid's media_tag with preload="none" and
 *      NO autoplay attribute — the source URL is present but, thanks to preload="none",
 *      no bytes download until .play() is called here.
 * Behaviour:
 *   - The poster paints immediately; nothing downloads on load.
 *   - On first entry into the viewport the <source data-src> (if any) is hydrated and
 *     the video starts playing (which triggers the download).
 *   - It pauses when it leaves the viewport and resumes on re-entry.
 *   - Gallery videos always autoplay: if something else pauses one while it is in view
 *     (global.js pauseAllMedia() on thumbnail change / modal close), it restarts, unless
 *     a modal is open over it.
 * Fallback: no IntersectionObserver -> play everything on load.
 */
(function () {
  function hydrate(video) {
    if (video.dataset.hydrated) return;
    video.dataset.hydrated = '1';
    var sources = video.querySelectorAll('source[data-src]');
    for (var i = 0; i < sources.length; i++) {
      sources[i].src = sources[i].getAttribute('data-src');
    }
    video.load();
  }

  function play(video) {
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {}); // ignore autoplay rejections
  }

  var OPEN_MODAL = 'modal-dialog[open], product-modal[open], quick-add-modal[open], quick-view-modal[open]';

  function resumeIfVisible(event) {
    var video = event.target;
    if (!video.dataset.inView) return;
    setTimeout(function () {
      if (video.paused && video.dataset.inView && !document.querySelector(OPEN_MODAL)) play(video);
    }, 0);
  }

  // Custom banner videos (data-lazy-video) + product gallery videos (media_tag,
  // preload="none", no autoplay) inside the gallery list.
  var SELECTOR = 'video[data-lazy-video], .product__media-list video[preload="none"]';

  function init() {
    if (!('IntersectionObserver' in window)) {
      var all = document.querySelectorAll(SELECTOR);
      for (var i = 0; i < all.length; i++) {
        hydrate(all[i]);
        play(all[i]);
      }
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting) {
          video.dataset.inView = '1';
          hydrate(video);   // download the video the first time it comes into view
          play(video);
        } else if (video.dataset.hydrated) {
          delete video.dataset.inView;
          video.pause();    // save CPU/battery while off-screen
        }
      });
    }, { threshold: 0.25 });

    function watch(root) {
      var videos = root.matches && root.matches(SELECTOR) ? [root] : root.querySelectorAll(SELECTOR);
      for (var j = 0; j < videos.length; j++) {
        if (videos[j].dataset.lazyWatched) continue;
        videos[j].dataset.lazyWatched = '1';
        videos[j].addEventListener('pause', resumeIfVisible);
        observer.observe(videos[j]);
      }
    }

    watch(document);

    // Variant changes replace the gallery's innerHTML (product-info.js updateMedia), so pick up
    // videos added after load.
    new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          if (added[n].nodeType === 1) watch(added[n]);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });

    // pauseAllMedia() runs before ModalDialog.hide() finishes, so re-check once a modal closes.
    document.body.addEventListener('modalClosed', function () {
      setTimeout(function () {
        if (document.querySelector(OPEN_MODAL)) return;
        var videos = document.querySelectorAll(SELECTOR);
        for (var k = 0; k < videos.length; k++) {
          if (videos[k].dataset.inView && videos[k].paused) play(videos[k]);
        }
      }, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
